import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type FeedbackAttachment, feedbackAttachmentUrl } from '@/entities/feedback';
import { Dialog, Icons } from '@/shared/ui';

import { formatBytes } from './labels';
import styles from './AttachmentList.module.css';

/**
 * Files of a suggestion or a message. Images show a thumbnail that opens a larger preview;
 * every other file is a plain download (never rendered in the page).
 */
export function AttachmentList({ attachments }: { attachments: readonly FeedbackAttachment[] }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<FeedbackAttachment | null>(null);
  if (attachments.length === 0) return null;
  return (
    <>
      <ul className={styles.list} aria-label={t('feedback.attach.listLabel')}>
        {attachments.map((file) => (
          <li key={file.id} className={styles.item}>
            {file.is_image ? (
              <button
                type="button"
                className={styles.imageButton}
                aria-label={t('feedback.attach.open', { name: file.filename })}
                onClick={() => {
                  setOpen(file);
                }}
              >
                <img
                  className={styles.thumb}
                  src={feedbackAttachmentUrl(file.id, 'thumb')}
                  alt={file.filename}
                  loading="lazy"
                />
              </button>
            ) : (
              <a className={styles.file} href={feedbackAttachmentUrl(file.id, 'download')} download>
                <span className={styles.fileIcon} aria-hidden>
                  <Icons.file />
                </span>
                <span className={styles.fileMeta}>
                  <span className={styles.fileName}>{file.filename}</span>
                  <span className={styles.size}>{formatBytes(file.size_bytes, i18n.language)}</span>
                </span>
              </a>
            )}
          </li>
        ))}
      </ul>
      {open && (
        <Dialog
          title={open.filename}
          size="xl"
          onClose={() => {
            setOpen(null);
          }}
        >
          <img
            className={styles.preview}
            src={feedbackAttachmentUrl(open.id, 'medium')}
            alt={open.filename}
          />
          <p className={styles.previewActions}>
            <a href={feedbackAttachmentUrl(open.id, 'original')} target="_blank" rel="noreferrer">
              {t('feedback.attach.original')}
            </a>
            {' · '}
            <a href={feedbackAttachmentUrl(open.id, 'download')} download>
              {t('feedback.attach.download')}
            </a>
          </p>
        </Dialog>
      )}
    </>
  );
}
